import {
    ArrowBack as ArrowBackIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

const CourseReceipt = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const receiptRef = useRef();
    const [searchParams] = useSearchParams();
    const apiURL = process.env.REACT_APP_API;

    // State management
    const [receiptData, setReceiptData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get enquiryid from URL
    const enquiryid = searchParams.get('enquiryid');

    // Fetch receipt data from API
    useEffect(() => {
        const fetchReceiptData = async () => {
            if (!enquiryid) {
                setError('Enquiry ID is missing');
                setLoading(false);
                enqueueSnackbar('Enquiry ID is required', { variant: 'error' });
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`${apiURL}/public/enquiry/receipt`, {
                    params: { enquiryid }
                });

                if (response.data && response.data.status === true) {
                    setReceiptData(response.data.data);
                    setError(null);
                } else {
                    setError(response.data?.message || 'Failed to fetch receipt data');
                    enqueueSnackbar(response.data?.message || 'Failed to fetch receipt data', { variant: 'error' });
                }
            } catch (err) {
                console.error('Error fetching receipt data:', err);
                setError(err.response?.data?.message || 'An error occurred while fetching receipt data');
                enqueueSnackbar(err.response?.data?.message || 'Failed to load receipt', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchReceiptData();
    }, [enquiryid, apiURL, enqueueSnackbar]);

    const handleBack = () => {
        navigate(-1);
    };

    const handlePrint = useReactToPrint({
        content: () => receiptRef.current,
        documentTitle: `Receipt-${receiptData?.enq_student_name || 'Student'}`,
        onAfterPrint: () => enqueueSnackbar('Receipt printed!', { variant: 'success' })
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateDiscount = (maxPrice, price) => {
        return Math.round(((maxPrice - price) / maxPrice) * 100);
    };

    const discount = receiptData ? calculateDiscount(receiptData.enq_course_amount_max, receiptData.enq_coures_amount) : 0;

    // Loading state
    if (loading) {
        return (
            <Box sx={{ py: 3 }}>
                <Container maxWidth="md">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Loading receipt...
                            </Typography>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        );
    }

    // Error state
    if (error || !receiptData) {
        return (
            <Box sx={{ py: 3 }}>
                <Container maxWidth="md">
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error || 'Receipt data not found'}
                    </Alert>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                    >
                        Go Back
                    </Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 3 }}>
            <Container maxWidth="md">
                {/* Header with Actions */}
                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                        >
                            Print Receipt
                        </Button>
                    </Stack>
                </Box>

                {/* Receipt Content */}
                <Box ref={receiptRef}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
                        {/* Receipt Header */}
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                Course Enrollment Receipt
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Receipt #ENQ-{new Date().getFullYear()}-{Math.floor(Math.random() * 1000).toString().padStart(3, '0')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Generated: {new Date().toLocaleDateString()}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Student Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Student Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Full Name
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.enq_student_name}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Gender
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.enq_student_gender}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Mobile
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.enq_student_mobile}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Email
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.enq_student_email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Course Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Course Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Course Name
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.c_name} - ({receiptData.c_title})
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Duration
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {receiptData.c_duration} months
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Course ID
                                            </Typography>
                                            <Typography variant="body2">
                                                {receiptData.course_id}
                                            </Typography>
                                        </Box>

                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Payment Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Payment Details
                            </Typography>
                            <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Original Fee
                                                </Typography>
                                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                                    {formatCurrency(receiptData.enq_course_amount_max)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Discount
                                                </Typography>
                                                <Typography variant="body2" color="success.main">
                                                    {discount}% OFF
                                                </Typography>
                                            </Box>
                                            {receiptData.c_coupon && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Coupon Applied
                                                    </Typography>
                                                    <Typography variant="body2" color="secondary.main" fontWeight={600}>
                                                        {receiptData.c_coupon} &mdash; {formatCurrency(receiptData.c_coupon_amount)} off
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{
                                            bgcolor: 'grey.50',
                                            p: 2,
                                            borderRadius: 1
                                        }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Final Amount
                                            </Typography>
                                            <Typography variant="h6" fontWeight={700} color="primary.main">
                                                {formatCurrency(receiptData.enq_coures_amount)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Box>

                        {/* Franchise Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Franchise Information
                            </Typography>
                            <Card variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Franchise Name
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {receiptData.fr_name}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Owner
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {receiptData.fr_owner_name}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email
                                                </Typography>
                                                <Typography variant="body2">
                                                    {receiptData.fr_email}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Address
                                                </Typography>
                                                <Typography variant="body2">
                                                    {receiptData.fr_address}, {receiptData.fr_city}, {receiptData.fr_state}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Franchise Code
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {receiptData.fr_code}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Box>

                        {/* Additional Info */}
                        <Box sx={{ mt: 3 }}>
                            <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Referral Source: {receiptData.enq_student_from}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Enrollment Date: {formatDate(receiptData.enq_date)}
                                </Typography>
                            </Stack>
                        </Box>

                        {/* Footer Note */}
                        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" align="center">
                                This is an official receipt for course enrollment.
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Print Notice */}
                <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }}>
                    Click "Print Receipt" to generate a printable version.
                </Alert>

                {/* Course Content Section */}
                {receiptData.website_content && (
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, mt: 3 }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Course Content
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box
                            sx={{
                                '& h1, & h2, & h3': { fontWeight: 600, mt: 2, mb: 1 },
                                '& h2': { fontSize: '1.25rem' },
                                '& p': { mb: 1.5, lineHeight: 1.7, color: 'text.secondary' },
                                '& strong': { color: 'text.primary' },
                                '& br': { display: 'block', mb: 1 }
                            }}
                            dangerouslySetInnerHTML={{ __html: receiptData.website_content }}
                        />
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default CourseReceipt;